<template>
    <div v-for="step in 3" :key="step" class="step-container" :class="{ 'w-full': step != 3, 'custom' : (step <= currentStep) }">
        <!-- show check icon if step is completed -->
        <div
            v-if="step <= currentStep"
            class="checked-icon"
            :class="[statusBgColor(step), statusLabelColorForNotReach(step)]"
        >
            <!-- Show X icon for failed status at step 3, or step 2 if in_progress_at is null -->
            <svg v-if="(exposure.status === 'failed' && step === 3) || (step === 2 && exposure.in_progress_at === null && step <= currentStep)" xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path
                    d="M9.5 2.5L2.5 9.5M2.5 2.5L9.5 9.5"
                    stroke="white"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                />
            </svg>
            <!-- Show check icon for successful completion -->
            <svg v-else class="relative top" xmlns="http://www.w3.org/2000/svg" width="12" height="9" viewBox="0 0 12 9" fill="none">
                <path
                    d="M11.4053 2.26138L5.38652 8.28013C5.24588 8.42067 5.05519 8.49962 4.85636 8.49962C4.65753 8.49962 4.46684 8.42067 4.32621 8.28013L0.969018 4.90513C0.828615 4.76451 0.749756 4.57392 0.749756 4.37521C0.749756 4.17649 0.828615 3.9859 0.969018 3.84528L1.90652 2.90778C2.04708 2.76781 2.23737 2.68922 2.43574 2.68922C2.63411 2.68922 2.82439 2.76781 2.96496 2.90778L4.87511 4.76028L9.41074 0.28044C9.55132 0.140323 9.74171 0.0616455 9.94019 0.0616455C10.1387 0.0616455 10.3291 0.140323 10.4696 0.28044L11.4048 1.19638C11.4751 1.26607 11.531 1.34899 11.5691 1.44037C11.6072 1.53175 11.6268 1.62977 11.6269 1.72878C11.6269 1.82779 11.6074 1.92583 11.5693 2.01724C11.5313 2.10865 11.4755 2.19162 11.4053 2.26138Z"
                    fill="white"
                />
            </svg>
        </div>

        <!-- show number with loading spinner if this is the next step -->
        <div
            v-else-if="step === nextStep"
            class="loading-number"
            :class="[statusBgColor(step), statusLabelColorForNotReach(step)]"
        >
            <div class="loading-spinner"></div>
            <span class="number-text">{{ step }}</span>
        </div>

        <!-- show number if status not reached -->
        <div v-else class="status-number" :class="[statusBgColor(step)]">
            {{ step }}
        </div>

        <p class="step-label" v-if="step === 1" :class="[statusLabelColor(1), { done: step < currentStep }]">Detected</p>
        <p class="step-label" v-if="step === 2" :class="[statusLabelColor(2), { done: step < currentStep }]">Request Sent</p>
        <p class="step-label" v-if="step === 3" :class="[statusLabelColor(3), { done: step < currentStep }]">Closed</p>

        <div class="step-connector" :class="{ 
            'bg-green-700': step < currentStep && !(step === 2 && exposure.status === 'failed') && !(step === 1 && exposure.in_progress_at === null), 
            'bg-red-700': (step === 2 && step < currentStep && exposure.status === 'failed') || (step === 1 && exposure.in_progress_at === null && currentStep >= 2)
        }" v-if="step < 3"></div>
    </div>
</template>

<script setup lang="ts">
const props = defineProps<{
    exposure: any;
}>();

//computed status index
const currentStep = computed(() => {
    if (props.exposure.status === 'pending') return 1;
    if (props.exposure.status === 'in_progress') return 2;
    if (props.exposure.status === 'closed') return 3;
    if (props.exposure.status === 'failed') return 3;
    return 1;
});

//computed next step (the upcoming step)
const nextStep = computed(() => {
    if (props.exposure.status === 'pending') return 2;
    if (props.exposure.status === 'in_progress') return 3;
    return null; // No next step if already closed/failed
});

//function that returns the status color
const statusLabelColor = (step: number) => {
    // If it's a failed status at step 3, return red
    if (props.exposure.status === 'failed' && step === 3) return 'text-red-600';
    // If step 2 failed (in_progress_at is null), return red
    if (step === 2 && props.exposure.in_progress_at === null && step <= currentStep.value) return 'text-red-600';
    // If step is completed or current, return green
    if (step <= currentStep.value) return 'text-success-600';
    // If step is the next step, return gray (loading state)
    if (step === nextStep.value) return 'text-zinc-600';
    // Otherwise return zinc (gray)
    return 'text-zinc-600';
};

const statusLabelColorForNotReach = (step: number) => {
    // Completed steps and current step should have white text on colored background
    if (step <= currentStep.value) return 'text-white';
    // Next step (loading) should have gray text on gray background
    if (step === nextStep.value) return 'text-zinc-600';
    return 'text-zinc-600';
};

//function that returns the status bg-color
const statusBgColor = (step: number) => {
    // If it's a failed status at step 3, return red background
    if (props.exposure.status === 'failed' && step === 3) return 'bg-red-600';
    // If step 2 failed (in_progress_at is null), return red background
    if (step === 2 && props.exposure.in_progress_at === null && step <= currentStep.value) return 'bg-red-600';
    // Completed steps and current step should have green background
    if (step <= currentStep.value) return 'bg-success-600';
    // Next step should have white background (loading state)
    if (step === nextStep.value) return 'bg-white';
    // Otherwise return white
    return 'bg-white';
};
</script>

<style scoped lang="scss">
.step-container {
    @apply flex gap-1 md:gap-3 items-center select-none;

    .step-label {
        @apply text-xs md:text-sm font-medium whitespace-nowrap;

        &.done {
            @apply hidden md:block;
        }
    }
}

.step-connector {
    @apply h-0.5 flex-1 md:w-full bg-zinc-300 w-full;
}

.checked-icon {
    @apply h-6 w-6 rounded-full flex items-center justify-center;
}

.status-number {
    @apply rounded-full w-6 h-6 flex items-center justify-center border border-zinc-300 text-center font-bold text-sm;
}

.loading-number {
    @apply rounded-full w-6 h-6 flex items-center justify-center border border-zinc-300 text-center font-bold text-sm relative;
    
    .number-text {
        @apply z-10 text-zinc-600 font-bold text-sm;
    }
    
    .loading-spinner {
        @apply absolute inset-0 rounded-full border-2 border-zinc-300 border-t-zinc-500 animate-spin;
    }
}

.status-not-reached {
    @apply rounded-full w-6 h-6 flex items-center justify-center text-center font-bold text-sm text-zinc-600;
    @apply border border-zinc-300;
}

.step-connector.bg-green-700 {
    @apply bg-success-600;
}

.step-connector.bg-red-700 {
    @apply bg-red-600;
}
</style>
